import { useLocation, useNavigate } from "react-router-dom";
import { useSharedStatus } from "../lib/StatusContext";
import type { VolumeView } from "../lib/status";
import { useHashTarget } from "../lib/motion";
import { serviceUrl } from "../lib/links";
import { TopbarSettings } from "../components/TopbarSettings";
import { bytesLabel, Chevron, PageSkeleton, Row, SI, Stat } from "../components/status/atoms";

function storageTile(volumes: VolumeView[] | undefined): {
  freeText: string;
  hint: string;
  level?: "ok" | "warn" | "fault";
} {
  const usable = (volumes ?? []).filter((v) => v.level !== "unknown");
  if (!usable.length) return { freeText: "—", hint: "no volumes configured" };
  const free = usable.reduce((n, v) => n + (v.ok ? v.freeBytes : 0), 0);
  const rank = { fault: 3, warn: 2, ok: 1, unknown: 0 } as const;
  const worst = [...usable].sort(
    (a, b) => rank[b.level] - rank[a.level] || b.usedPct - a.usedPct,
  )[0]!;
  return {
    freeText: bytesLabel(free),
    hint: `${worst.label}: ${worst.usedPct}% used`,
    level: worst.level === "ok" || worst.level === "unknown" ? undefined : worst.level,
  };
}

export function OverviewView() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const flashing = useHashTarget(hash);
  const { data, error, loading } = useSharedStatus();
  const faults = data?.alerts.filter((a) => a.level === "fault").length ?? 0;
  const storage = storageTile(data?.volumes);
  const down = data?.services.filter((s) => !s.ok).length ?? 0;
  const subtitle = data?.ok
    ? "All services responding"
    : `${faults} fault${faults === 1 ? "" : "s"}${down ? ` · ${down} service${down === 1 ? "" : "s"} down` : ""}`;
  const health = (
    <span className={`pill ${data?.ok ? "pill--ok" : "pill--fault"}`}>
      <span className={`dot ${data?.ok ? "dot--ok dot--live" : "dot--fault"}`} />
      {data?.ok ? "Healthy" : "Attention"}
    </span>
  );
  const queueLive = Boolean(
    data?.downloads.ok && data.downloads.queueCount > 0 && !data.downloads.paused,
  );

  if (loading && !data) return <PageSkeleton title="Overview" stats={4} />;

  return (
    <div className="page">
      <header className="topbar">
        <h1 className="topbar__title">Overview</h1>
        <div className="topbar__right">
          <TopbarSettings />
        </div>
      </header>

      <div className="page-body">
        <div className="page-inner stack stagger">
          {error && <div className="banner banner--fault">Could not reach the console API: {error}</div>}

          <div className="page-head__trail">
            Homelab <span>›</span> <b>DESKTOP-NIGSLMN</b>
          </div>
          <div className="page-health">
            {health}
            <span className="page-health__detail">{subtitle}</span>
          </div>

          <div className="stats stats--4">
            <Stat
              label="Faults"
              level={faults ? "fault" : "ok"}
              severityValue
              value={faults}
              hint={faults ? "see alerts below" : "nothing failing"}
              onClick={faults ? () => navigate("/overview#alerts") : undefined}
              icon={
                <svg {...SI}>
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4.5M12 16h.01" />
                </svg>
              }
            />
            <Stat
              label="Awaiting a decision"
              level={data?.pendingCount ? "warn" : undefined}
              value={data?.pendingCount ?? 0}
              hint={data?.pendingCount ? "open the inbox" : "inbox clear"}
              onClick={() => navigate("/decide")}
              icon={
                <svg {...SI}>
                  <path d="M4 7h16M4 12h16M4 17h9" />
                </svg>
              }
            />
            <Stat
              label="In queue"
              value={data?.queueCount ?? 0}
              unit={data?.downloads.ok ? ` · ${data.downloads.speedText}` : undefined}
              hint="open downloads"
              live={queueLive}
              onClick={() => navigate("/downloads#downloads")}
              icon={
                <svg {...SI}>
                  <path d="M12 4v11m0 0 4-4m-4 4-4-4" />
                  <path d="M5 19h14" />
                </svg>
              }
            />
            <Stat
              label="Storage"
              level={storage.level}
              text={storage.freeText}
              unit=" free"
              hint={storage.hint}
              onClick={() => navigate("/stack#storage")}
              icon={
                <svg {...SI}>
                  <path d="M4 7h16v10H4z" />
                  <path d="M4 11h16" />
                </svg>
              }
            />
          </div>

          <div id="alerts" className={`stack ${flashing === "alerts" ? "is-flash" : ""}`}>
            {data?.alerts.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`alert alert--${a.level} ${a.href ? "alert--go" : ""}`}
                onClick={() => a.href && navigate(a.href)}
              >
                <span className="alert__mark" aria-hidden="true" />
                <div className="grow">
                  <strong>{a.title}</strong>
                  <span className="truncate">{a.body}</span>
                </div>
                {a.href && <Chevron />}
              </button>
            ))}
            {data && !data.alerts.length && (
              <div className="alert alert--ok">
                <span className="alert__mark" aria-hidden="true" />
                <div className="grow">
                  <strong>Everything looks healthy</strong>
                  <span>No failing services, volumes, or hosts.</span>
                </div>
              </div>
            )}
          </div>

          {data && data.pendingCount > 0 && (
            <button type="button" className="btn btn--primary" onClick={() => navigate("/decide")}>
              Review {data.pendingCount} item{data.pendingCount === 1 ? "" : "s"} waiting on you
            </button>
          )}

          {data?.playback.sessions.length ? (
            <section className="card section" id="playback">
              <div className="section__head">
                <h2>Now playing</h2>
                <span className="pill pill--accent">{data.playback.sessions.length} streaming</span>
              </div>
              {data.playback.sessions.map((s, i) => (
                <Row
                  key={i}
                  href={serviceUrl(data.services.find((x) => x.role === "playback")?.url, "/web")}
                  title="Open Plex"
                >
                  <span className="dot dot--ok dot--live" />
                  <div className="grow">
                    <div className="truncate">
                      <strong>{s.title}</strong>
                    </div>
                    <div className="section__sub truncate">
                      {s.user} · {s.player} · {s.state}
                      {s.transcoding ? " · transcoding" : " · direct play"}
                    </div>
                  </div>
                </Row>
              ))}
            </section>
          ) : null}

          {data && (
            <div className="stamp">
              <span className="dot dot--ok dot--live" />
              Updated {new Date(data.generatedAt).toLocaleTimeString()} · {data.mode}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
